// Detect, then correct (slice 14; design/09 §7): the map the table already
// shows is read in a worker, and what it finds becomes the grid's locked
// lines on the edges the DM chose — nothing else changes. The worker is
// behind a seam so the panel can be graded in jsdom, which has no workers.
import { axisFromDetection, type Grid } from '@dragons-table/vtt-shared';
import { pictureBlob } from '../assets';
import type { DetectReply } from './detectWorker';

export type DetectRunner = (blob: Blob) => Promise<DetectReply>;

/** The real runner: a module worker, given thirty seconds, then put away. */
export const workerRunner: DetectRunner = (blob) => new Promise((resolve) => {
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') { resolve({ ok: false, reason: 'this browser cannot read a map in the background' }); return; }
  const worker = new Worker(new URL('./detectWorker.ts', import.meta.url), { type: 'module' });
  const timer = setTimeout(() => { worker.terminate(); resolve({ ok: false, reason: 'reading the map took too long' }); }, 30_000);
  worker.onmessage = (e: MessageEvent<DetectReply>) => { clearTimeout(timer); worker.terminate(); resolve(e.data); };
  worker.onerror = () => { clearTimeout(timer); worker.terminate(); resolve({ ok: false, reason: 'the map could not be read' }); };
  worker.postMessage({ blob });
});

export type DetectOutcome =
  | { kind: 'placed'; grid: Grid; x: number | null; y: number | null; ms: number }
  | { kind: 'none'; reason: string };

/**
 * Read the map and place what was found on the current grid: each axis that
 * was detected becomes its locked lines, counted from the edge the DM set;
 * an axis that was not keeps what it had; the appearance is untouched.
 */
export async function detectOnMap(mapId: string, grid: Grid, run: DetectRunner = workerRunner, fetchImpl: typeof fetch = fetch): Promise<DetectOutcome> {
  // The bytes the board already fetched, never a fetch of its `blob:` URL: the deployed CSP refuses that.
  const blob = await pictureBlob(mapId, fetchImpl);
  if (!blob) return { kind: 'none', reason: 'the map could not be fetched' };
  let reply: DetectReply;
  try { reply = await run(blob); } catch (err) { return { kind: 'none', reason: err instanceof Error ? err.message : 'the map could not be read' }; }
  if (!reply.ok) return { kind: 'none', reason: reply.reason };
  const { x, y } = reply.detection;
  if (!x && !y) return { kind: 'none', reason: 'no printed grid stood out on this map' };
  const next: Grid = {
    ...grid,
    x: x ? axisFromDetection(x, grid.x.edge, reply.width) : grid.x,
    y: y ? axisFromDetection(y, grid.y.edge, reply.height) : grid.y,
  };
  return { kind: 'placed', grid: next, x: x ? next.x.anchors.length : null, y: y ? next.y.anchors.length : null, ms: reply.ms };
}

/** What the DM is told: how many lines were placed on each axis, and that the editor is how to correct them. */
export function detectMessage(o: DetectOutcome): string {
  if (o.kind === 'none') return `Nothing placed: ${o.reason}. Set the grid by hand below.`;
  const part = (n: number | null, what: string) => (n === null ? `no ${what} lines found (that axis is unchanged)` : `${n} ${what} line${n === 1 ? '' : 's'} locked`);
  return `${part(o.x, 'vertical')}, ${part(o.y, 'horizontal')}. Drag or unlock any that are wrong; Reset axis undoes it.`.replace(/^./, (c) => c.toUpperCase());
}
